import {
  getAuth,
  getMultiFactorResolver,
  MultiFactorError,
  MultiFactorResolver,
  multiFactor,
  onIdTokenChanged,
  signInWithEmailAndPassword,
  signOut as firebaseSignOut,
  TotpMultiFactorGenerator,
  TotpSecret,
  User,
} from "firebase/auth";
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  StaffAccess,
  getStaffAccess,
  getStaffAuthMessage,
  normalizeStaffEmail,
  requiresAdminMfaEnrollment,
} from "../lib/auth-contract";
import { firebaseApp } from "../lib/firebase";
import { requestStaffAuthEmail } from "../lib/staff-invitations";

type StaffAuthStatus =
  | "loading"
  | "signed-out"
  | "authenticated"
  | "email-verification-required"
  | "mfa-enrollment-required"
  | "mfa-challenge-required"
  | "unauthorized";

type StaffIdentity = {
  uid: string;
  email: string;
  displayName: string;
  access: Extract<StaffAccess, { authorized: true }>;
  enrolledFactorCount: number;
};

type MfaEnrollment = { qrCodeUrl: string; secretKey: string };

type StaffAuthContextValue = {
  status: StaffAuthStatus;
  identity: StaffIdentity | null;
  mfaEnrollment: MfaEnrollment | null;
  busy: boolean;
  error: string | null;
  signIn: (email: string, password: string) => Promise<boolean>;
  signOut: () => Promise<void>;
  sendPasswordReset: (email: string) => Promise<boolean>;
  resendVerification: () => Promise<void>;
  beginMfaEnrollment: () => Promise<void>;
  completeMfaEnrollment: (code: string) => Promise<boolean>;
  completeMfaSignIn: (code: string) => Promise<boolean>;
  clearError: () => void;
};

const StaffAuthContext = createContext<StaffAuthContextValue | null>(null);

export function StaffAuthProvider({ children }: { children: ReactNode }) {
  const auth = useMemo(() => getAuth(firebaseApp), []);
  const resolverRef = useRef<MultiFactorResolver | null>(null);
  const secretRef = useRef<TotpSecret | null>(null);
  const [status, setStatus] = useState<StaffAuthStatus>("loading");
  const [identity, setIdentity] = useState<StaffIdentity | null>(null);
  const [mfaEnrollment, setMfaEnrollment] = useState<MfaEnrollment | null>(
    null,
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const applyUser = useCallback(async (user: User | null) => {
    if (!user) {
      setIdentity(null);
      setMfaEnrollment(null);
      setStatus("signed-out");
      return;
    }

    if (!user.emailVerified) {
      setIdentity(null);
      setStatus("email-verification-required");
      return;
    }

    const token = await user.getIdTokenResult();
    const access = getStaffAccess(token.claims);
    if (!access.authorized) {
      setIdentity(null);
      setStatus("unauthorized");
      return;
    }

    const nextIdentity = toStaffIdentity(user, access);
    setIdentity(nextIdentity);
    setStatus(
      requiresAdminMfaEnrollment(access.role, nextIdentity.enrolledFactorCount)
        ? "mfa-enrollment-required"
        : "authenticated",
    );
  }, []);

  useEffect(
    () =>
      onIdTokenChanged(auth, (user) => {
        void applyUser(user).catch(() => {
          setIdentity(null);
          setError(
            "Central couldn’t verify this staff account. Try signing in again.",
          );
          setStatus("unauthorized");
        });
      }),
    [applyUser, auth],
  );

  const signIn = useCallback(
    async (email: string, password: string) => {
      setBusy(true);
      setError(null);
      try {
        await signInWithEmailAndPassword(
          auth,
          normalizeStaffEmail(email),
          password,
        );
        return true;
      } catch (caught) {
        const code = getFirebaseErrorCode(caught);
        if (code === "auth/multi-factor-auth-required") {
          const resolver = getMultiFactorResolver(
            auth,
            caught as MultiFactorError,
          );
          if (
            resolver.hints.some(
              (hint) => hint.factorId === TotpMultiFactorGenerator.FACTOR_ID,
            )
          ) {
            resolverRef.current = resolver;
            setStatus("mfa-challenge-required");
            return true;
          }
        }
        setError(getStaffAuthMessage(code));
        return false;
      } finally {
        setBusy(false);
      }
    },
    [auth],
  );

  const sendPasswordReset = useCallback(
    async (email: string) => {
      setBusy(true);
      setError(null);
      try {
        await requestStaffAuthEmail(
          normalizeStaffEmail(email),
          "password_reset",
        );
        return true;
      } catch (caught) {
        const code = getFirebaseErrorCode(caught);
        setError(getStaffAuthMessage(code, "email"));
        return false;
      } finally {
        setBusy(false);
      }
    },
    [auth],
  );

  const beginMfaEnrollment = useCallback(async () => {
    if (!auth.currentUser) return;
    setBusy(true);
    setError(null);
    try {
      const session = await multiFactor(auth.currentUser).getSession();
      const secret = await TotpMultiFactorGenerator.generateSecret(session);
      secretRef.current = secret;
      setMfaEnrollment({
        qrCodeUrl: secret.generateQrCodeUrl(
          auth.currentUser.email ?? undefined,
          "Central",
        ),
        secretKey: secret.secretKey,
      });
    } catch (caught) {
      setError(getStaffAuthMessage(getFirebaseErrorCode(caught)));
    } finally {
      setBusy(false);
    }
  }, [auth]);

  const completeMfaEnrollment = useCallback(
    async (code: string) => {
      if (!auth.currentUser || !secretRef.current) return false;
      setBusy(true);
      setError(null);
      try {
        const assertion = TotpMultiFactorGenerator.assertionForEnrollment(
          secretRef.current,
          code.trim(),
        );
        await multiFactor(auth.currentUser).enroll(
          assertion,
          "Authenticator app",
        );
        secretRef.current = null;
        setMfaEnrollment(null);
        await auth.currentUser.getIdToken(true);
        await applyUser(auth.currentUser);
        return true;
      } catch (caught) {
        setError(getStaffAuthMessage(getFirebaseErrorCode(caught)));
        return false;
      } finally {
        setBusy(false);
      }
    },
    [applyUser, auth],
  );

  const completeMfaSignIn = useCallback(async (code: string) => {
    const resolver = resolverRef.current;
    const hint = resolver?.hints.find(
      (candidate) => candidate.factorId === TotpMultiFactorGenerator.FACTOR_ID,
    );
    if (!resolver || !hint) return false;
    setBusy(true);
    setError(null);
    try {
      const assertion = TotpMultiFactorGenerator.assertionForSignIn(
        hint.uid,
        code.trim(),
      );
      await resolver.resolveSignIn(assertion);
      resolverRef.current = null;
      return true;
    } catch (caught) {
      setError(getStaffAuthMessage(getFirebaseErrorCode(caught)));
      return false;
    } finally {
      setBusy(false);
    }
  }, []);

  const signOut = useCallback(async () => {
    resolverRef.current = null;
    secretRef.current = null;
    setMfaEnrollment(null);
    setError(null);
    await firebaseSignOut(auth);
  }, [auth]);

  const value = useMemo<StaffAuthContextValue>(
    () => ({
      status,
      identity,
      mfaEnrollment,
      busy,
      error,
      signIn,
      signOut,
      sendPasswordReset,
      resendVerification: async () => {
        if (auth.currentUser?.email)
          await requestStaffAuthEmail(auth.currentUser.email, "verify_email");
      },
      beginMfaEnrollment,
      completeMfaEnrollment,
      completeMfaSignIn,
      clearError: () => setError(null),
    }),
    [
      auth,
      beginMfaEnrollment,
      busy,
      completeMfaEnrollment,
      completeMfaSignIn,
      error,
      identity,
      mfaEnrollment,
      sendPasswordReset,
      signIn,
      signOut,
      status,
    ],
  );

  return (
    <StaffAuthContext.Provider value={value}>
      {children}
    </StaffAuthContext.Provider>
  );
}

export function useStaffAuth() {
  const value = useContext(StaffAuthContext);
  if (!value)
    throw new Error("useStaffAuth must be used within StaffAuthProvider");
  return value;
}

function toStaffIdentity(
  user: User,
  access: Extract<StaffAccess, { authorized: true }>,
): StaffIdentity {
  return {
    uid: user.uid,
    email: user.email ?? "",
    displayName:
      user.displayName?.trim() || user.email?.split("@")[0] || "Staff member",
    access,
    enrolledFactorCount: multiFactor(user).enrolledFactors.length,
  };
}

function getFirebaseErrorCode(error: unknown) {
  if (
    typeof error === "object" &&
    error &&
    "code" in error &&
    typeof error.code === "string"
  ) {
    return error.code;
  }
  return "auth/unknown";
}
